import {
  ContentList,
  ContentListSortingType,
  ProviderIdentifier
} from '@norlys-a-s/norlys-play-kmm';
import { Area } from '@norlys-a-s/norlys-play-kmm';

import {
  ContentCover,
  ContentItem,
  ContentLayout,
  ContentListProviders,
  ContentType,
  StreamType,
  UIElementContent,
  UIElementStyle,
  UniverseSortingValue
} from '@smart-tv/typings/content';
import defaultMovieCover from '@smart-tv/scaffold/images/placeholder_default_portrait.svg';
import defaultSeriesCover from '@smart-tv/scaffold/images/placeholder_default_landscape.svg';
import { generateUID } from '@smart-tv/utils/uidGenerator';
import { PageType } from '@smart-tv/components/stripes/_utils/types';
import { NpvrEventItem } from '@smart-tv/typings/npvr';
import { NpvrSliderItem } from '@smart-tv/components/npvr/npvr-slider';

export const prefixImageUrl = (url: string): string => {
  if (url.indexOf('//') === -1) return `https://${url}`;

  const { protocol } = new URL(url);

  if (protocol === 'http:') return url.replace(/^http:/, 'https:');

  return url;
};

export const getDefaultCoverByContentType = (type: ContentType): string => {
  return type === ContentType.Movies ? defaultMovieCover : defaultSeriesCover;
};

export interface RawUiElementContentItem {
  cover?: string;
  channelIconUrl?: string;
  contentId?: string;
  contentType: ContentType;
  endtime?: string;
  eventId?: string;
  isCustomerFavorite?: boolean;
  coverIcon?: 'rent' | 'subscription';
  rentOnlyProvider?: boolean;
  inUserSubScription?: boolean;
  npvrInfo?: {
    customerRecording: boolean;
  };
  providerCollectionCover?: string;
  providerCollectionId?: string;
  providerCollectionDefaultUniverse?: string;
  providerCollectionName?: string;
  providerCount?: number;
  providerLogoOverlay?: string;
  sid?: string;
  starttime?: string;
  thumbnail: string;
  secondaryThumbnail: string;
  heroImage: string;
  title: string;
  genre: {
    mainGenreId: string;
    mainGenreName: string;
    subGenres: string[];
    subGenreNames: string[];
  };
  year?: number;
}

export const getLayout = (style: UIElementStyle) => {
  switch (style) {
    case UIElementStyle.ProgramPortrait:
    case UIElementStyle.UniverseMovies:
    case UIElementStyle.Portrait:
      return ContentLayout.Portrait;

    case UIElementStyle.ProgramLandscape:
      return ContentLayout.Landscape;

    case UIElementStyle.ProgramChannel:
      return ContentLayout.WideLandscape;

    case UIElementStyle.UniverseMixed:
    case UIElementStyle.Mixed:
      return ContentLayout.Mixed;

    case UIElementStyle.Provider:
      return ContentLayout.ProviderBox;

    default:
      return ContentLayout.Landscape;
  }
};

export const getContentType = (item: RawUiElementContentItem) => {
  if (item.sid) return ContentType.Channel;

  if (item.providerCollectionName) return ContentType.Provider;

  return item.contentType;
};

export const getStyle = (imageType: string) => {
  switch (imageType) {
    case 'PORTRAIT':
      return UIElementStyle.ProgramPortrait;

    case 'LANDSCAPE':
      return UIElementStyle.ProgramLandscape;

    case 'MIXED':
      return UIElementStyle.UniverseMixed;

    default:
      return UIElementStyle.ProgramLandscape;
  }
};

export const getCoverType = (item: RawUiElementContentItem, style: UIElementStyle) => {
  if (item.contentType === ContentType.Movies) {
    return ContentCover.Portrait;
  }

  if (style === UIElementStyle.ProgramPortrait || style === UIElementStyle.UniverseMovies) {
    return ContentCover.Portrait;
  }

  if (style === UIElementStyle.ProgramLandscape) {
    return ContentCover.Landscape;
  }

  if (style === UIElementStyle.Provider) {
    return ContentCover.ProviderBox;
  }

  if (style === UIElementStyle.ProgramChannel) {
    return ContentCover.Square;
  }

  if (item.contentType === ContentType.Series) {
    return ContentCover.Landscape;
  }

  return ContentCover.Landscape;
};

export function toContentType(rawType: string | null | undefined): ContentType {
  return rawType === ContentType.Series ? ContentType.Series : ContentType.Movies;
}

export function toContentListProviders(providers: string | undefined): ContentListProviders {
  return providers === 'SUBSCRIBED' ? ContentListProviders.Subscribed : ContentListProviders.All;
}

export function toProviderIdentifier(providers: ContentListProviders): ProviderIdentifier {
  return providers === ContentListProviders.All
    ? ProviderIdentifier.ALL
    : ProviderIdentifier.SUBSCRIBED;
}

export function toSortingType(sortingType?: UniverseSortingValue): ContentListSortingType {
  return sortingType === UniverseSortingValue.NEWEST
    ? ContentListSortingType.NEWEST
    : ContentListSortingType.VIEWS;
}

export function getCoverTypeForContent(
  contentType: ContentType,
  style: UIElementStyle
): ContentCover {
  if (
    style === UIElementStyle.ProgramPortrait ||
    style === UIElementStyle.UniverseMovies ||
    style === UIElementStyle.Portrait ||
    contentType === ContentType.Movies
  ) {
    return ContentCover.Portrait;
  }

  return ContentCover.Landscape;
}

export function getAreaForPageType(
  pageType: PageType,
  universeId?: number,
  providerId?: number
): Area {
  if (pageType === PageType.Universe && universeId) {
    return new Area.UniverseArea(universeId);
  } else if (pageType === PageType.ProviderUniverse && providerId) {
    return new Area.ProviderCollectionArea(providerId);
  }
  return Area.Frontpage;
}

export function mapContentListItem(
  item: ContentList.ContentData,
  style: UIElementStyle
): ContentItem {
  const contentType = toContentType(item.contentType);
  const layout = getLayout(style);
  const cover = item.cover ? prefixImageUrl(item.cover) : getDefaultCoverByContentType(contentType);

  return {
    key: generateUID(),
    contentId: item.contentId ?? undefined,
    contentType,
    contentLayout: layout,
    cover,
    coverType: getCoverTypeForContent(contentType, style),
    duration: 0,
    favorited: item.isCustomerFavorite ?? undefined,
    heroImage: item.heroImage ?? undefined,
    logo: item.providerLogoOverlay ?? undefined,
    providerCount: item.providerCount ?? undefined,
    title: item.title ?? undefined
  };
}

export function mapNpvrEventToCardProps(event: NpvrEventItem, canPlay: boolean): NpvrSliderItem {
  const metaParts: string[] = [];
  if (event.metadataSeasonNo !== null) metaParts.push(`Sæson ${event.metadataSeasonNo}`);
  if (event.metadataEpisodeNo !== null) metaParts.push(`Episode ${event.metadataEpisodeNo}`);

  const progress =
    event.progressInSeconds > 0 && event.eventLength > 0
      ? Math.min(100, (event.progressInSeconds / event.eventLength) * 100)
      : undefined;

  const errorMessage =
    event.errorMessage ||
    (event.customerRecordingError !== 'none' ? event.customerRecordingError : undefined);

  return {
    id: String(event.eventId),
    headline: event.title,
    metaLine: metaParts.length > 0 ? metaParts.join(', ') : undefined,
    duration: event.readableDuration ?? undefined,
    dateLabel: event.readableRecordingTime ?? undefined,
    durationInSeconds: event.eventLength ?? undefined,
    expiryLabel: canPlay ? event.readableExpireTime ?? undefined : undefined,
    coverUrl: event.coverImageUrl ? prefixImageUrl(event.coverImageUrl) : undefined,
    providerIconUrl: event.channelIcon ? prefixImageUrl(event.channelIcon) : undefined,
    seriesRecording: event.npvrInfo?.isSeriesRecording === true,
    seriesId: event.metadataSerieId ?? undefined,
    progress,
    errorMessageText: errorMessage,
    errorMessageBadge:
      event.npvrInfo?.isEnoughSpaceForRecording || canPlay || errorMessage
        ? undefined
        : 'Ikke nok ledig plads til at optage',
    streamOptions:
      event.sid !== null && event.eventId !== null && canPlay
        ? {
            streamType: StreamType.CatchUp,
            streamParams: {
              sid: event.sid ?? undefined,
              eventId: event.eventId ?? undefined,
              progress: event.progressInSeconds ?? 0
            }
          }
        : undefined
  };
}

export function toUIElementContent(result: ContentList, style: UIElementStyle): UIElementContent {
  const contentData = result.contentData?.asJsReadonlyArrayView() ?? [];
  const contentStripeInfo = result.contentStripeInfo;
  const showAllElementPosition = contentStripeInfo?.showAllAtElementCell;

  const items = contentData.reduce<ContentItem[]>((acc, item) => {
    if (item) acc.push(mapContentListItem(item, style));
    return acc;
  }, []);

  const contentTotalCount = contentStripeInfo?.contentTotalCount ?? items.length;

  const showAllElementCell =
    showAllElementPosition !== null &&
    showAllElementPosition !== undefined &&
    showAllElementPosition > 0 &&
    showAllElementPosition < contentTotalCount;

  return {
    id: 0,
    title: '',
    layout: getLayout(style),
    contentTotalCount,
    showAllElementCell: {
      show: showAllElementCell,
      position: showAllElementPosition ?? 0
    },
    items,
    updateNext: 0,
    properties: {
      showContentTitle: true
    }
  };
}
